import { catalog, movies, tvSeries, collections, homeHeroSlides } from "@/mocks/catalog";

export interface CatalogItem {
  id: string;
  title: string;
  category: "movie" | "tvseries";
  genres: string[];
  region: string;
  year: number;
  rating: number;
  duration?: number;
  episodes?: number;
  director: string;
  actors: string[];
  releaseDate: string;
  summary: string;
  synopsis: string;
  tags: string[];
  poster: string;
  backdrop: string;
}

export interface FilterState {
  genres: string[];
  regions: string[];
  years: number[];
  ratingMin: number | null;
}

export type SortKey = "newest" | "rating" | "popular";

export const allItems = catalog as CatalogItem[];
export const movieItems = movies as CatalogItem[];
export const tvItems = tvSeries as CatalogItem[];
export const homeSlides = homeHeroSlides;
export const homeCollections = collections;

export const GENRE_OPTIONS = [
  "动作",
  "科幻",
  "悬疑",
  "犯罪",
  "剧情",
  "爱情",
  "古装",
  "历史",
  "战争",
  "喜剧",
  "家庭",
  "冒险",
  "都市",
  "医疗",
  "年代",
  "武侠",
  "职场",
  "美食",
  "治愈",
  "惊悚",
];

export const REGION_OPTIONS = [
  "中国大陆",
  "中国香港",
  "美国",
  "英国",
  "日本",
  "法国",
];

export const YEAR_OPTIONS = [2025, 2024, 2023];

export const RATING_OPTIONS = [
  { label: "8.5 分以上", value: 8.5 },
  { label: "8.0 分以上", value: 8 },
  { label: "7.5 分以上", value: 7.5 },
];

export const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "newest", label: "最新上线" },
  { key: "rating", label: "评分最高" },
  { key: "popular", label: "人气最热" },
];

export const emptyFilters = (): FilterState => ({
  genres: [],
  regions: [],
  years: [],
  ratingMin: null,
});

export function getItemById(id: string | undefined): CatalogItem | undefined {
  if (!id) return undefined;
  return allItems.find((item) => item.id === id);
}

export function getRelated(item: CatalogItem, limit = 6): CatalogItem[] {
  const scored = allItems
    .filter((candidate) => candidate.id !== item.id)
    .map((candidate) => {
      let score = 0;
      if (candidate.category === item.category) score += 2;
      score += candidate.genres.filter((genre) => item.genres.includes(genre)).length * 3;
      score += candidate.tags.filter((tag) => item.tags.includes(tag)).length * 2;
      if (candidate.region === item.region) score += 1;
      return { candidate, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  const list = scored.map((entry) => entry.candidate);
  if (list.length < limit) {
    const filler = allItems
      .filter((candidate) => candidate.id !== item.id && !list.includes(candidate))
      .sort((a, b) => b.rating - a.rating);
    list.push(...filler);
  }
  return list.slice(0, limit);
}

export function matchesQuery(item: CatalogItem, query: string): boolean {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return true;
  const haystack = [
    item.title,
    item.summary,
    item.synopsis,
    item.director,
    ...item.genres,
    ...item.actors,
    ...item.tags,
    item.region,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(keyword);
}

export function searchItems(query: string): CatalogItem[] {
  const keyword = query.trim();
  if (!keyword) return [];
  return allItems.filter((item) => matchesQuery(item, keyword));
}

export function applyFilters(items: CatalogItem[], filters: FilterState): CatalogItem[] {
  return items.filter((item) => {
    if (filters.genres.length && !filters.genres.some((genre) => item.genres.includes(genre))) {
      return false;
    }
    if (filters.regions.length && !filters.regions.includes(item.region)) {
      return false;
    }
    if (filters.years.length && !filters.years.includes(item.year)) {
      return false;
    }
    if (filters.ratingMin !== null && item.rating < filters.ratingMin) {
      return false;
    }
    return true;
  });
}

export function sortItems(items: CatalogItem[], sort: SortKey): CatalogItem[] {
  const list = [...items];
  if (sort === "rating") {
    return list.sort((a, b) => b.rating - a.rating);
  }
  if (sort === "popular") {
    return list.sort((a, b) => b.rating * 10 + b.year - (a.rating * 10 + a.year));
  }
  return list.sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return b.releaseDate.localeCompare(a.releaseDate);
  });
}

export function countActiveFilters(filters: FilterState): number {
  return (
    filters.genres.length +
    filters.regions.length +
    filters.years.length +
    (filters.ratingMin !== null ? 1 : 0)
  );
}

export function formatMeta(item: CatalogItem): string {
  if (item.category === "movie") {
    return `${item.duration} 分钟`;
  }
  return `全 ${item.episodes} 集`;
}